import { useRouter } from 'next/router';

import { IS_LOCAL } from '@/constants/env';
import { DynamicLinkOptions } from '@/shared/types';
import Airbridge from '@/utils/airbridge';
import Firebase from '@/utils/firebase';
import { getPageName } from '@/utils/logger';

const AIRBRIDGE_PARAM_PREFIX = 'ab_';

const sanitizeEventName = (eventName: string): string => {
  return eventName.replace(/[^\w]/g, '_');
};

const formatOptionsForLogging = (options: DynamicLinkOptions) => {
  return Object.fromEntries(
    Object.entries(options).map(([key, value]) => [
      `${AIRBRIDGE_PARAM_PREFIX}${key}`,
      typeof value === 'number' ? value.toString() : value,
    ])
  );
};

interface UseAirbridgeDynamicReturn {
  navigateWithTracking: (eventName: string, options: DynamicLinkOptions, isNeedStorePageLink?: boolean) => void;
  getDynamicLink: (options: DynamicLinkOptions, isNeedStorePageLink?: boolean) => string;
  logToFirebase: (eventName: string, options: DynamicLinkOptions) => void;
}

const useAirbridgeDynamicLink = (channel = 'web_organic'): UseAirbridgeDynamicReturn => {
  const router = useRouter();

  const getDynamicLink = (options: DynamicLinkOptions, isNeedStorePageLink = false) => {
    return Airbridge.getDynamicLink({ channel, ...options }, isNeedStorePageLink);
  };

  const logToFirebase = (eventName: string, options: DynamicLinkOptions) => {
    const pageName = getPageName(router);
    const sanitizedEventName = sanitizeEventName(eventName);
    const formattedOptions = formatOptionsForLogging(options);
    const payload = { page_name: pageName, ...formattedOptions };

    if (IS_LOCAL) {
      console.log('logger >>', { eventName: sanitizedEventName, ...payload });
      return;
    }

    Firebase.logEventOnlyFirebase(sanitizedEventName, payload);
  };

  const navigateWithTracking = (eventName: string, options: DynamicLinkOptions, isNeedStorePageLink = false) => {
    logToFirebase(eventName, options);

    Airbridge.setDynamicLink(
      {
        channel,
        ...options,
      },
      isNeedStorePageLink
    );
  };

  return {
    navigateWithTracking,
    getDynamicLink,
    logToFirebase,
  };
};

export default useAirbridgeDynamicLink;
